"use client";

import ControlledTable from "@/components/controlled-table";
import { useTableServer } from "@/hooks/useTableServer";
import PushNotificationRepository from "@/utilities/repositories/PushNotification";
import { IResponseData } from "@/utilities/types/requests";
import {
  EPushNotificationStatus,
  IPushNotification,
} from "@/utilities/types/PushNotification";
import useApi from "@/hooks/useApi";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getColumns } from "./columns";
import FilterElement from "./filter-element";
import PushNotificationDetailDrawer from "../detail-drawer";

const POLL_INTERVAL_MS = 3000;

export default function PushNotificationTable() {
  const [detailItem, setDetailItem] = useState<IPushNotification | null>(null);

  const { request: cancelNotification } = useApi<
    IResponseData<IPushNotification>
  >({
    request: PushNotificationRepository.cancel,
  });

  const onHeaderCellClick = (value: string) => ({
    onClick: () => {
      handleSort(value);
    },
  });

  const {
    isLoading,
    tableData,
    currentPage,
    totalItems,
    handlePaginate,
    pageSize,
    handleChangePageSize,
    searchTerm,
    handleSearch,
    sortConfig,
    handleSort,
    filters,
    updateFilter,
    handleReset,
    refresh,
  } = useTableServer<IPushNotification>({
    server: {
      request: PushNotificationRepository.list,
    },
    searchBy: (searchTerm) => {
      const s = searchTerm || undefined;
      return { search: s };
    },
  });

  // Poll while any row is in SENDING status to detect SENT/FAILED transition.
  const hasSending = useMemo(
    () =>
      tableData.some(
        (item: IPushNotification) =>
          item.status === EPushNotificationStatus.SENDING,
      ),
    [tableData],
  );

  useEffect(() => {
    if (!hasSending) return;
    const interval = window.setInterval(() => {
      refresh();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [hasSending, refresh]);

  const onCancelItem = useCallback(
    async (id: string) => {
      try {
        await cancelNotification(id);
        refresh();
      } catch {
        // toast handled by useApi
      }
    },
    [cancelNotification, refresh],
  );

  const onViewDetail = useCallback((row: IPushNotification) => {
    setDetailItem(row);
  }, []);

  const columns = useMemo(
    () =>
      getColumns({
        data: tableData,
        sortConfig,
        onHeaderCellClick,
        onCancelItem,
        onViewDetail,
      }),
    [
      onHeaderCellClick,
      sortConfig,
      onCancelItem,
      onViewDetail,
      tableData,
    ],
  );

  return (
    <div className="@container mt-4">
      <FilterElement
        onSearch={handleSearch}
        searchTerm={searchTerm}
        status={filters?.status}
        fromDate={filters?.fromDate}
        toDate={filters?.toDate}
        updateFilter={updateFilter}
        handleReset={handleReset}
      />
      <ControlledTable
        variant="modern"
        data={tableData}
        isLoading={isLoading}
        showLoadingText
        // @ts-expect-error - ControlledTable column type is loose; our columns match shape
        columns={columns}
        paginatorOptions={{
          pageSize,
          setPageSize: handleChangePageSize,
          total: totalItems,
          current: currentPage,
          onChange: (page: number) => handlePaginate(page),
        }}
        className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-60 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_.rc-table-row:last-child_td.rc-table-cell]:border-b-0 [&_thead.rc-table-thead]:border-t-0"
      />
      <PushNotificationDetailDrawer
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onAfterChange={() => {
          setDetailItem(null);
          refresh();
        }}
      />
    </div>
  );
}
